import React, {useEffect, useMemo} from 'react'
import {useDispatch, useSelector} from 'react-redux'
import {useNavigate, useParams} from 'react-router-dom'
import {Controller, useForm} from 'react-hook-form'
import {joiResolver} from '@hookform/resolvers/joi'
import {VmoForm, VmoFormField, VmoFormInput, VmoFormSelect, VmoFormTextArea, VmoTooltip} from 'vmo-library'
import {getUsers} from 'actions/settings'
import Page from 'components/common/Page'
import {createTeams, editTeams, getAllTeams, getTeamsData} from 'actions/settings_organisations'
import {CREATE_TEAMS, EDIT_TEAMS} from 'actions/types'
import UserImage from 'components/common/UserImage'
import {getRoleInfo} from 'utils/local-storage'
import {teamSchema} from 'validation/settings/my-organization/teams.schema'
import {AiOutlineInfoCircle} from 'react-icons/ai'
import useLearnMoreUrl from 'hooks/pure/useLearnMoreUrl'
import {SETTINGS_PATH} from 'constants/path'
import {getPathWithPrefix} from 'utils/helper'
import {Input, TextArea} from 'components/common/Form'
import {useUnsavedChanges} from 'hooks/pure/useUnsavedChanges'

const defaultValues = {
  name: '',
  assigneeIds: [],
  description: '',
}

const TeamsDetails = ({toggleModal}) => {
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const {id: editId} = useParams()

  const {setWarningModal} = useUnsavedChanges()

  const {
    handleSubmit,
    formState: {errors, isDirty},
    control,
    reset,
    clearErrors,
  } = useForm({
    mode: 'onTouched',
    shouldFocusError: true,
    reValidateMode: 'onChange',
    submitFocusError: true,
    shouldUnregister: false,
    resolver: joiResolver(teamSchema),
    defaultValues,
  })

  const {teamsData} = useSelector(state => state.settingsOrganisation)

  const {getUsersList = []} = useSelector(state => state.settings)
  const {type, isLoadingData, successLabels = []} = useSelector(state => state.apiReducer)

  const {settingsLearnMoreUrl} = useLearnMoreUrl()
  const addTeamsLearnMore = settingsLearnMoreUrl?.myOrganization?.teams?.addLearnMore
  const updateTeamsLearnMore = settingsLearnMoreUrl?.myOrganization?.teams?.updateLearnMore

  const userRole = getRoleInfo()?.role || ''
  const allowManageTeamsRole = useMemo(() => {
    return ['owner', 'admin'].includes(userRole)
  }, [userRole])

  useEffect(() => {
    if (editId) dispatch(getTeamsData(editId))
    dispatch(getUsers())
    return () => {
      dispatch({type: 'CLEAR_TEAM_DATA'})
    }
  }, [])

  useEffect(() => {
    if (successLabels.includes(CREATE_TEAMS)) {
      dispatch(getAllTeams())
      toggleModal(false)
    }

    if (successLabels.includes(EDIT_TEAMS)) {
      setWarningModal(false)
      dispatch(getAllTeams())
      navigate(-1)
    }
  }, [successLabels])

  useEffect(() => {
    if (teamsData) {
      const {id, ...data} = teamsData
      reset(data)
    }
  }, [teamsData])

  useEffect(() => {
    if (editId && isDirty) {
      setWarningModal(true)
    }
    return () => {
      if (editId && isDirty) setWarningModal(false)
    }
  }, [isDirty])

  const userList = getUsersList?.users?.map(user => {
    const {id, firstName, lastName, avatar, email} = user

    return {
      key: id,
      value: id,
      text: `${firstName} ${lastName}`,
      content: (
        <div>
          <UserImage profile={avatar && user} className="ui avatar image" />
          {`${firstName} ${lastName} `}
          <small className="ml-2 vmo-lead">{email}</small>
        </div>
      ),
    }
  })

  const submitForm = data => {
    if (editId) {
      dispatch(editTeams(editId, data))
    } else {
      dispatch(createTeams(data))
    }
  }

  return (
    <Page
      header={editId ? 'Edit Team' : 'Create Team'}
      headerbackbtn={{
        onClick: () => {
          if (editId) {
            setWarningModal(false)
            navigate(getPathWithPrefix(SETTINGS_PATH.PREFIX, SETTINGS_PATH.TEAMS))
          } else {
            toggleModal(false)
          }
        },
      }}
      learnMoreUrl={editId ? updateTeamsLearnMore : addTeamsLearnMore}
      subHeader="Create and edit your team for efficient utilization of users"
      table={false}
      headerDivider
      lockRole={!allowManageTeamsRole}
      actionButton={{
        cancel: {
          content: 'Cancel',
          disabled: isLoadingData && (type.includes(CREATE_TEAMS) || type.includes(EDIT_TEAMS)),
          onClick: () => {
            if (editId) {
              setWarningModal(false)
              navigate(getPathWithPrefix(SETTINGS_PATH.PREFIX, SETTINGS_PATH.TEAMS))
            } else {
              toggleModal(false)
            }
          },
        },
        success: {
          content: editId ? 'Update' : 'Create',
          loading: isLoadingData && (type.includes(CREATE_TEAMS) || type.includes(EDIT_TEAMS)),
          ...(allowManageTeamsRole && {
            onClick: handleSubmit(submitForm),
          }),
          disabled: !isDirty || (isLoadingData && (type.includes(CREATE_TEAMS) || type.includes(EDIT_TEAMS))),
        },
      }}
    >
      <VmoForm onSubmit={handleSubmit(submitForm)} className="errorLabel">
        <Input
          name="name"
          control={control}
          label="Team Name"
          placeholder="Mention your team name"
          maxLength={40}
          required
          width={8}
          tooltipProps={{
            content: 'Mention your team name',
          }}
        />

        <VmoFormField required width={8}>
          <div className="info-header">
            <label>Team Members</label>
            <VmoTooltip content="Add members in your team" trigger={<AiOutlineInfoCircle />} />
          </div>
          <Controller
            control={control}
            name="assigneeIds"
            render={({field: {value, onChange}}) => (
              <VmoFormSelect
                search
                multiple
                onClick={() => clearErrors(['assigneeIds'])}
                options={userList}
                value={value}
                onChange={(e, {value}) => onChange(value)}
                error={
                  errors.assigneeIds && {
                    content: errors.assigneeIds.message,
                  }
                }
              />
            )}
          />
        </VmoFormField>

        <TextArea
          name="description"
          control={control}
          label="Description"
          tooltipProps={{content: 'Specify brief description about the team'}}
          maxLength={500}
          style={{resize: 'none'}}
          width={8}
        />
      </VmoForm>
    </Page>
  )
}

export default TeamsDetails
